import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mail, FileText, Loader2, Paperclip, AlertTriangle, Copy, Eye } from 'lucide-react';
import { money } from './poLine';
import { getCompanyProfile } from '../../../lib/companyProfile';

const EmailPreview = ({ preview, onClose }) => (
  <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { onClose(); } }}>
    <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="email-po-preview-dialog">
      {preview && (
        <>
          <div className="px-5 pt-5 pb-3 border-b text-sm space-y-0.5">
            <DialogTitle className="text-base">{preview.subject}</DialogTitle>
            <p className="text-slate-600"><span className="text-slate-400 w-16 inline-block">From</span>{preview.from_name}</p>
            <p className="text-slate-600"><span className="text-slate-400 w-16 inline-block">To</span>{preview.to}</p>
            {preview.reply_to && <p className="text-slate-600"><span className="text-slate-400 w-16 inline-block">Reply to</span>{preview.reply_to}</p>}
            <p className="text-slate-600 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5 text-slate-400" />{preview.attachment}</p>
          </div>
          <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="w-full h-[60vh] bg-slate-100" data-testid="email-po-preview-frame" />
        </>
      )}
    </DialogContent>
  </Dialog>
);

const EmailPODialog = ({ open, onOpenChange, po, recipients, onSend, onPreview, onPreviewEmail, copyTo }) => {
  const [selected, setSelected] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  React.useEffect(() => {
    if (open) {
      setSelected(recipients[0]?.id || '');
      setNote('');
    }
  }, [open, recipients]);

  if (!po) return null;
  const chosen = recipients.find((r) => r.id === selected);

  const handleSend = async () => {
    setSending(true);
    try { await onSend({ contact_id: selected, note }); } finally { setSending(false); }
  };

  const showEmail = async () => {
    const p = await onPreviewEmail({ contact_id: selected, note });
    if (p) { setPreview(p); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="email-po-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center"><Mail className="h-5 w-5 mr-2" />Email Purchase Order</DialogTitle>
          <DialogDescription>Send {po.po_number} to {po.supplier_name} as a PDF attachment</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Send to *</Label>
            {recipients.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm flex gap-2" data-testid="email-po-no-contacts">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800">No email address on file for <strong>{po.supplier_name}</strong>. Add a contact person and email in <strong>Suppliers → Edit</strong> or the supplier's <strong>Contacts</strong> tab.</p>
              </div>
            ) : (
              <div className="space-y-2 mt-1" data-testid="email-po-recipients">
                {recipients.map((r) => (
                  <label key={r.id} className={`flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer transition-colors ${selected === r.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`email-po-recipient-${r.id}`}>
                    <input type="radio" name="po-recipient" checked={selected === r.id} onChange={() => setSelected(r.id)} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.name || po.supplier_name}{r.role ? <span className="text-slate-500 font-normal"> · {r.role}</span> : null}</p>
                      <p className="text-xs text-slate-600 truncate">{r.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Subject</Label>
            <p className="text-sm border rounded-md px-3 py-2 bg-slate-50 text-slate-700" data-testid="email-po-subject">Purchase Order {po.po_number} from {getCompanyProfile()?.name || 'your company'}</p>
          </div>

          <div>
            <Label>Personal note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={4} placeholder="e.g. Please deliver before Friday — call Sam on arrival." data-testid="email-po-note" />
            <p className="text-xs text-slate-500 mt-1">Added to a standard, professional covering email with the PO summary and delivery instructions.</p>
          </div>

          <div className="bg-slate-50 p-3 rounded text-sm flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Paperclip className="h-4 w-4 text-slate-500 shrink-0" />
              <span className="font-medium truncate">{po.po_number}.pdf</span>
              <span className="text-slate-500 whitespace-nowrap">· {po.line_items.length} item(s) · {money(po.total, po.currency)}</span>
            </div>
            <Button variant="outline" size="sm" onClick={onPreview} data-testid="email-po-preview-pdf"><FileText className="h-4 w-4 mr-1" />Preview PDF</Button>
          </div>

          {copyTo && (
            <p className="text-xs text-slate-600 flex items-center gap-1.5" data-testid="email-po-copy-note"><Copy className="h-3.5 w-3.5" />A copy of the email and PDF will also be sent to <strong>{copyTo}</strong></p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="email-po-cancel">Cancel</Button>
            <Button variant="outline" className="flex-1" onClick={showEmail} disabled={!chosen} data-testid="email-po-preview-email"><Eye className="h-4 w-4 mr-2" />Preview email</Button>
            <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleSend} disabled={!chosen || sending} data-testid="email-po-send">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {sending ? 'Sending…' : `Send to ${chosen?.email || 'supplier'}`}
            </Button>
          </div>
        </div>
        <EmailPreview preview={preview} onClose={() => setPreview(null)} />
      </DialogContent>
    </Dialog>
  );
};

export default EmailPODialog;
